import { Spin, Tabs } from "antd";
import { FunctionComponent } from "react";
import { RouteComponentProps } from "react-router-dom";
import BackTextComponent from "src/components/BackTextComponent";
import { ContainerBox } from "src/components/container-box";
import { isNullOrUndefinedOrEmpty } from "../../../common/utils";
import { AuthZTable } from "../../../components/authZTable";
import { PageTitle } from "../../../components/page-title";
import { TextWithSubTitle } from "../../../components/textWithSubTitle";
import { useSystemUserGroupDetailController } from "./controller";
import "./index.scss";
import { SystemUserGroupMemberRecord } from "./model/view-object";
import { FilterDropdown } from "src/components/filter-dropdown/index";

export type SystemUserGroupDetailProps = RouteComponentProps<{
  assetId: string;
  systemId: string;
  groupId: string;
}>;

export const SystemUserGroupMember = ({
  systemUserGroupMemberRecords,
  columns
}) => {
  return (
    <div className={"GroupMemberDetails"}>
      <AuthZTable
        className={"group-member-details"}
        id={"system-user-group-member"}
        columns={columns}
        dataSource={systemUserGroupMemberRecords}
      />
    </div>
  );
};

// @ts-ignore
export const SystemUserGroupDetail: FunctionComponent<SystemUserGroupDetailProps> = (
  props: SystemUserGroupDetailProps
) => {
  const { assetId, systemId, groupId } = props.match.params;
  const {
    filteredSystemUserGroupMemberRecords,
    groupName,
    backButtonClickFunctionality,
    systemName,
    isLoading,
    getResourceName,
    scopeToBeFiltered,
    scopesRelatedToSystemUserGroup,
    setScopeToBeFiltered,
  } = useSystemUserGroupDetailController({ assetId, systemId, groupId });
  const columns = [
    {
      title: "Group members",
      key: "name",
      render: (record: SystemUserGroupMemberRecord) => {
        return <TextWithSubTitle titles={[record.title(), record.employeeId]} />;
      },
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      sortEnabled: true,
      sorter: (recordA: SystemUserGroupMemberRecord, recordB: SystemUserGroupMemberRecord) => {
        const recordATitle = recordA.title();
        const recordBTitle = recordB.title();
        return recordATitle.localeCompare(recordBTitle);
      },
      searchEnabled: true,
      onFilter: (value: string, record: SystemUserGroupMemberRecord) => {
        const title = record.fullName ?? record.email;
        const employeeId = record.employeeId?.toString() ?? "";
        return title || employeeId
          ? title.toLowerCase().includes(value.toLowerCase()) ||
              employeeId.toLowerCase().includes(value.toLowerCase())
          : "";
      },
    },
    {
      title: "Role",
      key: "jobProfile",
      dataIndex: "jobProfile",
      render: (value) => {
        return <div>{value}</div>;
      },
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      searchEnabled: true,
      sortEnabled: true,
    },
    {
      title: "Member of other groups",
      key: "memberOfOtherGroups",
      dataIndex: "memberOfOtherGroups",
      render: (groups: string[]) => {
        if (isNullOrUndefinedOrEmpty(groups)) {
          return <div>--</div>;
        }
        return groups.map((value) => <div className={"member-of-other-groups"}>{value}</div>);
      },
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      searchEnabled: false,
      sortEnabled: false,
    },
    {
      title: "Current Scope as per security controls in system",
      key: "appliedScopes",
      dataIndex: "appliedScopes",
      render: (appliedScopes: string[]) => {
        if (isNullOrUndefinedOrEmpty(appliedScopes)) {
          return <div>--</div>;
        }
        return appliedScopes.map((value) => <div className={"applied-scopes"}>{value}</div>);
      },
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      searchEnabled: false,
      sortEnabled: false,
    },
    {
      title: "System resources",
      key: "systemResources",
      dataIndex: "systemResources",
      render: (systemResources: {}) => {
        return Object.keys(systemResources).map((type) => (
          <div className={"system-resources"}>
            {type} ({systemResources[type].length})
          </div>
        ));
      },
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      searchEnabled: false,
      sortEnabled: false,
    },
  ];

  function renderDropDownFilters() {
    return (
      <div className="drop-down-filters-container">
        <FilterDropdown
          title={"Scope"}
          placeholder="Select scope to be filtered"
          options={scopesRelatedToSystemUserGroup().map((availableScope) => {
            return { label: availableScope, value: availableScope };
          })}
          defaultValue={scopeToBeFiltered}
          onChange={(selectedValue: string) => {
            setScopeToBeFiltered(selectedValue);
          }}
        />
      </div>
    );
  }

  function renderTabs() {
    return (
      <div className={"tab-container"}>
        <Tabs type="editable-card" defaultActiveKey={"group-members"} hideAdd>
          <Tabs.TabPane tab={"Members"} key={"group-members"} closable={false}>
            <SystemUserGroupMember
              systemUserGroupMemberRecords={filteredSystemUserGroupMemberRecords}
              columns={columns}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  }

  const renderSystemUserGroupDetailPage = () => {
    if (filteredSystemUserGroupMemberRecords) {
      return (
        <div className="SystemUserGroupDetail">
          <Spin spinning={isLoading.valueOf()}>
            <ContainerBox>
              <BackTextComponent
                backText={getResourceName()}
                clickFunctionality={backButtonClickFunctionality}
              />
              <div className={"group-information"}>
                <PageTitle title={groupName.toString()} />
                <div className={"system-name"}>{systemName}</div>
                {renderDropDownFilters()}
                {renderTabs()}
              </div>
            </ContainerBox>
          </Spin>
        </div>
      );
    }
  };

  return renderSystemUserGroupDetailPage();
};
